'use client';

/**
 * DateRenderer Component
 *
 * Renders DATE, DATETIME, TIME field types.
 * Supports display, edit, and inline modes.
 */

import Box from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { format, isValid as isValidDate, parseISO } from 'date-fns';
import type React from 'react';
import { getNeonStyles, type NeonAccent } from '../../../theme/palette';
import { transitionPresets } from '../../../theme/transitions';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { formatDate, formatDateTime, formatTime } from '../../../utils/formatters';
import { transientOptions } from '../../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface DateRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTextFieldProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $hasError: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledTextField = styled(
  TextField,
  transientOptions,
)<StyledTextFieldProps>(({ theme, $isDark, $neonAccent, $hasError, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const sizeStyles = {
    small: { fontSize: '0.875rem', padding: '8px 12px' },
    medium: { fontSize: '1rem', padding: '12px 16px' },
    large: { fontSize: '1.125rem', padding: '16px 20px' },
  };

  const s = sizeStyles[$size];

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      transition: transitionPresets.input,

      '& input': {
        fontSize: s.fontSize,
        padding: s.padding,
      },

      '& fieldset': {
        borderColor: $hasError
          ? theme.palette.error.main
          : neonStyles
            ? neonStyles.border
            : $isDark
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(0, 0, 0, 0.15)',
        transition: transitionPresets.input,
      },

      '&:hover fieldset': {
        borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',

        '& fieldset': {
          borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 2,
          boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
      },
    },

    '& .MuiInputLabel-root': {
      color: $isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      fontSize: '0.875rem',

      '&.MuiInputLabel-shrink': {
        backgroundColor: $isDark ? '#1a1a1a' : '#ffffff',
        padding: '0 6px',
        marginLeft: '-4px',
      },

      '&.Mui-focused': {
        color: neonStyles ? neonStyles.base : theme.palette.primary.main,
      },
    },

    '& .MuiFormHelperText-root': {
      marginLeft: 4,
      marginTop: 6,
    },
  };
});

const DisplayValue = styled(
  Typography,
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
}));

const InlineInput = styled(
  TextField,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: 'transparent',
      transition: transitionPresets.input,

      '& fieldset': {
        borderColor: 'transparent',
      },

      '&:hover fieldset': {
        borderColor: neonStyles
          ? alpha(neonStyles.border, 0.5)
          : $isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        '& fieldset': {
          borderColor: neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 1,
        },
      },
    },

    '& input': {
      padding: '4px 8px',
    },
  };
});

// ============================================================================
// Helpers
// ============================================================================

const parseDate = (value: unknown): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value === 'string') {
    const parsed = parseISO(value);
    return isValidDate(parsed) ? parsed : null;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return isValidDate(date) ? date : null;
  }

  return null;
};

const getInputType = (fieldType: string): string => {
  switch (fieldType) {
    case 'DATETIME':
      return 'datetime-local';
    case 'TIME':
      return 'time';
    default:
      return 'date';
  }
};

const getInputValue = (date: Date | null, fieldType: string): string => {
  if (!date) return '';

  switch (fieldType) {
    case 'DATETIME':
      return format(date, "yyyy-MM-dd'T'HH:mm");
    case 'TIME':
      return format(date, 'HH:mm');
    default:
      return format(date, 'yyyy-MM-dd');
  }
};

// ============================================================================
// Component
// ============================================================================

export const DateRenderer: React.FC<DateRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const dateValue = parseDate(value);

  // Format display value based on field type
  const getDisplayValue = (): string => {
    if (!dateValue) return '-';

    switch (field.fieldType) {
      case 'DATETIME':
        return formatDateTime(dateValue);
      case 'TIME':
        return formatTime(dateValue);
      default:
        return formatDate(dateValue);
    }
  };

  // Handle value change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (!inputValue) {
      onChange?.(null);
      return;
    }

    // Parse the input value based on field type
    let newDate: Date | null = null;

    switch (field.fieldType) {
      case 'DATETIME':
        newDate = new Date(inputValue);
        break;
      case 'TIME': {
        // For time-only, create a date with today's date and the input time
        const [hours, minutes] = inputValue.split(':').map(Number);
        newDate = new Date();
        newDate.setHours(hours, minutes, 0, 0);
        break;
      }
      default:
        newDate = new Date(`${inputValue}T00:00:00`);
        break;
    }

    if (newDate && isValidDate(newDate)) {
      onChange?.(newDate.toISOString());
    }
  };

  // Display mode
  if (mode === 'display') {
    return (
      <Box className={className}>
        <DisplayValue variant="body1" $isDark={isDark}>
          {getDisplayValue()}
        </DisplayValue>
      </Box>
    );
  }

  // Inline mode
  if (mode === 'inline') {
    return (
      <InlineInput
        value={getInputValue(dateValue, field.fieldType)}
        onChange={handleChange}
        type={getInputType(field.fieldType)}
        disabled={disabled || field.disabled}
        error={!!error}
        size="small"
        fullWidth
        $isDark={isDark}
        $neonAccent={neonAccent}
        className={className}
        slotProps={{
          inputLabel: { shrink: true },
        }}
      />
    );
  }

  // Edit mode
  return (
    <StyledTextField
      value={getInputValue(dateValue, field.fieldType)}
      onChange={handleChange}
      type={getInputType(field.fieldType)}
      label={field.label}
      helperText={error || field.helpText}
      error={!!error}
      disabled={disabled || field.disabled}
      required={field.required}
      fullWidth
      variant="outlined"
      $isDark={isDark}
      $neonAccent={neonAccent}
      $hasError={!!error}
      $size={size}
      className={className}
      slotProps={{
        inputLabel: { shrink: true },
      }}
    />
  );
};

DateRenderer.displayName = 'DateRenderer';

export default DateRenderer;
